import { lazy, Suspense, useState, type ReactNode } from "react";
import { FileText } from "lucide-react";
import {
  credentialDocumentFormat,
  isCredentialDocumentImage,
} from "../../lib/credential-document-format";

const PdfThumbnail = lazy(() => import("./credential-pdf-thumbnail"));

export function CredentialDocumentThumbnail({
  url,
  fileName,
  title,
}: {
  url: string;
  fileName: string;
  title: string;
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const format = credentialDocumentFormat(fileName);
  if (failedUrl === url) {
    return (
      <ThumbnailMessage>
        Preview unavailable. Expand to retry or download the original.
      </ThumbnailMessage>
    );
  }
  if (format === "pdf") {
    return (
      <Suspense
        fallback={
          <ThumbnailMessage>Loading document preview…</ThumbnailMessage>
        }
      >
        <PdfThumbnail
          key={url}
          url={url}
          title={title}
          onError={() => setFailedUrl(url)}
        />
      </Suspense>
    );
  }
  if (isCredentialDocumentImage(fileName)) {
    return (
      <img
        src={url}
        alt={`Preview of ${title || fileName}`}
        // Keep credential documents out of session replays.
        data-sentry-block
        className="h-full w-full object-contain p-3"
        onError={() => setFailedUrl(url)}
      />
    );
  }
  return (
    <ThumbnailMessage>
      Download this {format.toUpperCase() || "document"} file to view the
      original.
    </ThumbnailMessage>
  );
}

function ThumbnailMessage({ children }: { children: ReactNode }) {
  return (
    <span className="flex h-full w-full flex-col items-center justify-center gap-3 p-6 text-center text-xs leading-relaxed text-slate-500">
      <FileText className="size-8 text-slate-400" aria-hidden="true" />
      {children}
    </span>
  );
}
